import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { homeForRole, spaceForRole, type Space } from '@/lib/homeForRole';
import { SessionUnavailable } from './SessionUnavailable';

/**
 * Renders a space only for the identities that belong to it, and sends
 * everyone else to their own space through homeForRole (RUI-13, RUI-17).
 * Decided from the auth store, before the space mounts its providers, so a
 * super admin or a viewer never loads the dialer to be bounced from it.
 */
export function RequireSpace({ space, children }: { space: Space; children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const active = useAuthStore((s) => s.activeMembership);
  const isLoading = useAuthStore((s) => s.isLoading);
  const unavailable = useAuthStore((s) => s.unavailable);
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }
  if (unavailable) return <SessionUnavailable />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/Login?next=${next}`} replace />;
  }
  const identity = { isSuperAdmin: user.isSuperAdmin, role: active?.role ?? null };
  if (spaceForRole(identity) !== space) return <Navigate to={homeForRole(identity)} replace />;
  return <>{children}</>;
}
